import axios from 'axios';

export interface S3PresignedUrlResponse {
  presigned_url: string;
  image_key: string;
  expires_in: number;
  expires_at: number;
}

export interface S3ImageUtilOptions {
  expires?: number; // Optional expiration time in seconds
}

class S3ImageUtils {
  private baseURL: string;
  private cache: Map<string, { url: string; expiresAt: number }> = new Map();

  constructor() {
    this.baseURL = import.meta.env.VITE_API_BASE_URL;
  }

  /**
   * Get presigned URL for an S3 image key
   * @param imageKey - The S3 key (e.g., "dev/68da921339024966b013cedd25a8ba2f.jpg")
   * @param options - Optional configuration
   * @returns Promise with the presigned URL
   */
  async getPresignedUrl(imageKey: string, options: S3ImageUtilOptions = {}): Promise<string> {
    if (!imageKey) {
      throw new Error('Image key is required');
    }

    // Check cache first
    const cacheKey = `${imageKey}_${options.expires || 3600}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && cached.expiresAt > Date.now()) {
      return cached.url;
    }

    try {
      // Build query parameters
      const params = new URLSearchParams({
        action: 'getPresignedUrl',
        key: imageKey,
      });

      if (options.expires) {
        params.append('expires', options.expires.toString());
      }

      // Get token for authorization
      const token = localStorage.getItem('token');
      
      const response = await axios.get<S3PresignedUrlResponse>(
        `${this.baseURL}/picture?${params.toString()}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            'Content-Type': 'application/json',
          },
        }
      );

      const { presigned_url, expires_at } = response.data;

      // Cache the result (expire 5 minutes before actual expiration for safety)
      const safeExpirationTime = (expires_at * 1000) - (5 * 60 * 1000);
      this.cache.set(cacheKey, {
        url: presigned_url,
        expiresAt: safeExpirationTime,
      });

      return presigned_url;
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      throw new Error('Failed to get image URL');
    }
  }

  /**
   * Clear cache for a specific image key or all cache
   * @param imageKey - Optional specific key to clear, if not provided clears all
   */
  clearCache(imageKey?: string): void {
    if (imageKey) {
      // Clear all cache entries for this specific image key
      const keysToDelete = Array.from(this.cache.keys()).filter(key => 
        key.startsWith(imageKey)
      );
      keysToDelete.forEach(key => this.cache.delete(key));
    } else {
      this.cache.clear();
    }
  }

  /**
   * Clean expired entries from cache
   */
  cleanExpiredCache(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.cache.forEach((value, key) => {
      if (value.expiresAt <= now) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Check if an image key is valid S3 format
   * @param imageKey - The image key to validate
   * @returns boolean
   */
  isValidS3Key(imageKey: string): boolean {
    return Boolean(imageKey && imageKey.includes('/') && imageKey.includes('.'));
  }
}

// Export singleton instance
export const s3ImageUtils = new S3ImageUtils();

/**
 * Utility function to get image URL - returns presigned URL for S3 keys or original URL for others
 * @param imageUrl - The image URL or S3 key
 * @param options - Optional configuration
 * @returns Promise with the final image URL
 */
export const getImageUrl = async (imageUrl: string, options: S3ImageUtilOptions = {}): Promise<string> => {
  if (!imageUrl) {
    return '';
  }

  // If it's already a full URL (starts with http/https), return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // If it looks like an S3 key, get presigned URL
  if (s3ImageUtils.isValidS3Key(imageUrl)) {
    try {
      return await s3ImageUtils.getPresignedUrl(imageUrl, options);
    } catch (error) {
      console.error('Failed to get presigned URL for:', imageUrl, error);
      return ''; // Return empty string if failed
    }
  }

  // For other cases, return as is (relative URLs, etc.)
  return imageUrl;
}; 